import { useEffect, useMemo } from "react";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { isTerminalBuildState } from "~/constants/BuildStates";
import NotEnoughData from "~/components/build/Show/components/Waterfall/EmptyStates/NotEnoughData";
import UpgradeRequired from "~/components/build/Show/components/Waterfall/EmptyStates/UpgradeRequired";
import IncompatibleBuild from "~/components/build/Show/components/Waterfall/EmptyStates/IncompatibleBuild";
import ViewNotAvailable from "~/components/build/Show/components/Waterfall/EmptyStates/ViewNotAvailable";
import { Build } from "~/components/build/Show/lib/types";
import { useParams } from "react-router-dom";
import Skeleton from "~/components/build/Show/components/Waterfall/EmptyStates/Skeleton";
import WaterfallRow from "~/components/build/Show/components/Waterfall/WaterfallRow";
import { getBuildkiteFrontendVersion } from "~/lib/frontendVersion";

import classNames from "classnames";

import { useWaterfallStore, WaterfallRowType } from "~/components/build/Show/lib/useWaterfallStore";
import { useFetch, CachePolicies } from "use-http";
import {
  FilterByOption,
  useFilterStore,
} from "~/components/build/Show/components/Sidebar/useFilterStore";
import { JOB_STATE_FILTERS } from "~/components/build/Show/lib/filterConstants";
import { useStepDependenciesStore } from "~/components/build/Show/lib/useStepDependenciesStore";

const useWaterfallData = () => {
  const { org, pipeline, number } = useParams();
  const waterfallDataUrl = `/${org}/${pipeline}/builds/${number}/waterfall_tab.json`;
  const store = useWaterfallStore();

  const { error, get, loading } = useFetch(waterfallDataUrl, {
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": window._csrf.token,
      "X-Buildkite-Frontend-Version": getBuildkiteFrontendVersion(),
    },
    cachePolicy: CachePolicies.CACHE_AND_NETWORK,
  });

  const fetchData = async () => {
    const data = await get();
    if (data) {
      store.setData(data);
    }
  };

  return { data: store.data, error, loading, fetchData };
};

function MissingData({ build }: { build: Build }) {
  if (isTerminalBuildState(build.state)) {
    return <IncompatibleBuild />;
  }
  return <NotEnoughData />;
}

export default function WaterfallPage({ allowCollapse = false }: { allowCollapse: boolean }) {
  const { store } = useBuild();

  if (Features.DisableStepsWaterfallView) {
    return <ViewNotAvailable />;
  }

  // Need to upgrade to see the waterfall
  if (!store.waterfallAvailable) {
    return <UpgradeRequired />;
  }

  return (
    <div className="relative min-w-0 flex-auto">
      <div
        className={classNames(
          "flex h-full w-full",
          Features.BuildSidebar ? "absolute overflow-y-auto" : "",
        )}
      >
        <WaterfallView allowCollapse={allowCollapse} />
      </div>
    </div>
  );
}

/** Check if a waterfall row matches any of the provided filters based on job state data. */
const rowMatchesFilters = (
  row: WaterfallRowType,
  filters: FilterByOption[],
  build: Build,
  dependenciesByStep: Record<string, { missing_dependencies?: unknown[] }>,
): boolean => {
  const { job_state, job_passed, job_soft_failed, step_uuid } = row;

  // If no job state data, can't filter (shouldn't happen for leaf rows)
  if (!job_state) {
    return false;
  }

  const jobData = { state: job_state, passed: job_passed, softFailed: job_soft_failed };

  return filters.some((filter) => {
    // Special case: steps with missing dependencies in terminal builds are considered failed
    if (filter === FilterByOption.Failed) {
      const missingDependencies = dependenciesByStep[step_uuid]?.missing_dependencies || [];
      if (isTerminalBuildState(build.state) && missingDependencies.length > 0) {
        return true;
      }
    }

    return JOB_STATE_FILTERS[filter](jobData);
  });
};

/** Filter the rows based on the active filters applied to job states. */
const filterRows = (
  data: WaterfallRowType[],
  filters: FilterByOption[],
  build: Build,
  dependenciesByStep: Record<string, { missing_dependencies?: unknown[] }>,
) => {
  if (filters.length === 0) {
    return data;
  }

  function filterChildren(children: WaterfallRowType[]): WaterfallRowType[] {
    return children.reduce<WaterfallRowType[]>((acc, child) => {
      if (child.children.length) {
        const filteredChildren = filterChildren(child.children);
        if (filteredChildren.length > 0) {
          acc.push({ ...child, children: filteredChildren });
        }
        return acc;
      }

      if (rowMatchesFilters(child, filters, build, dependenciesByStep)) {
        acc.push(child);
      }

      return acc;
    }, []);
  }

  return filterChildren(data);
};

const WaterfallView = ({ allowCollapse }: { allowCollapse: boolean }) => {
  const { build } = useBuild();

  const { data, loading, fetchData } = useWaterfallData();
  const filters = useFilterStore((state) => state.filterBy);
  const dependenciesByStep = useStepDependenciesStore((state) => state.dependenciesByStep);

  useEffect(() => {
    if (!Features.DisableStepsWaterfallView) {
      fetchData();
    }
  }, [build]);

  const filteredRows = useMemo(() => {
    if (!data) {
      return [];
    }
    return filterRows(data.chart_data, filters, build, dependenciesByStep);
  }, [data, filters, build, dependenciesByStep]);

  if (!data && loading) {
    return <Skeleton />;
  }

  if (!data) {
    return null;
  }

  if (data.chart_data.length === 0) {
    return <MissingData build={build} />;
  }

  return (
    <div className="flex-1" data-testid="waterfall">
      <section
        className={classNames(
          "relative m-0 flex w-full border-gray-400 contain-layout",
          Features.BuildSidebar ? "border-b" : "rounded-lg border",
        )}
      >
        <div className="bg-silver -m-[1px] w-full p-0">
          {filteredRows.map((row) => {
            const uniqueKey = [row.step_uuid, row.job_uuid];
            return (
              <WaterfallRow
                row={row}
                uniqueKey={uniqueKey}
                key={uniqueKey.join("-")}
                barContainerPadding={data.bar_container_padding}
                depth={0}
                allowCollapse={allowCollapse}
              />
            );
          })}
        </div>
      </section>
    </div>
  );
};
